import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useRBAC } from '../contexts/RBACContext';
import { useTranslation } from '../contexts/TranslationContext';
import { apiService } from '../services/ApiService';
import { calculatePasswordStrength } from '../utils/passwordValidation';
import { logger } from '../utils/log';
import { useUserPreference } from '../hooks/useUserPreference';

interface SimpleSettingsPanelProps {
  isVisible: boolean;
}

// DT page-header glyph (stroked by `.dt-phead .pic svg`) — the gear from the mockup.
const IC_SETTINGS = (
  <svg viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const SimpleSettingsPanel: React.FC<SimpleSettingsPanelProps> = ({ isVisible }) => {
  const { mode, toggleTheme } = useTheme();
  const { user, refreshToken } = useAuth();
  const { isAdmin } = useRBAC();
  const { t, currentLanguage, setLanguage, isRTL } = useTranslation();
  const [showAssetIcons2D, setShowAssetIcons2D] = useUserPreference<boolean>('showAssetIcons2D', false);

  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [organization, setOrganization] = useState(user?.organization || '');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  // OSS Settings state (admin only)
  const [ossClientId, setOssClientId] = useState('');
  const [ossClientSecret, setOssClientSecret] = useState('');
  const [ossConfigured, setOssConfigured] = useState(false);
  const [ossHasSecret, setOssHasSecret] = useState(false);
  const [ossTestStatus, setOssTestStatus] = useState<string | null>(null);
  const [ossLastTested, setOssLastTested] = useState<string | null>(null);
  const [ossSaving, setOssSaving] = useState(false);
  const [ossTesting, setOssTesting] = useState(false);
  const [ossMessage, setOssMessage] = useState('');
  const [showOssSecret, setShowOssSecret] = useState(false);

  // Load OSS settings on mount (admin only)
  React.useEffect(() => {
    if (isAdmin) {
      loadOssSettings();
    }
  }, [isAdmin]);

  const loadOssSettings = async () => {
    try {
      const response = await apiService.get('/settings/oss');
      if (response.configured) {
        setOssConfigured(true);
        setOssClientId(response.clientId || '');
        setOssHasSecret(response.hasSecret || false);
        setOssTestStatus(response.testStatus);
        setOssLastTested(response.lastTested);
      }
    } catch (error) {
      logger.debug('Error loading OSS settings:', error);
    }
  };

  const handleSaveOssSettings = async () => {
    if (!ossClientId || !ossClientSecret) {
      setOssMessage('Both Client ID and Client Secret are required');
      return;
    }

    setOssSaving(true);
    setOssMessage('');

    try {
      await apiService.post('/settings/oss', {
        clientId: ossClientId,
        clientSecret: ossClientSecret
      });
      setOssMessage('Credentials saved successfully!');
      setOssConfigured(true);
      setOssHasSecret(true);
      setOssClientSecret(''); // Clear secret from form
      setTimeout(() => setOssMessage(''), 3000);
    } catch (error: any) {
      setOssMessage(error.message || 'Failed to save credentials');
    } finally {
      setOssSaving(false);
    }
  };

  const handleTestOssCredentials = async () => {
    if (!ossClientId || (!ossClientSecret && !ossHasSecret)) {
      setOssMessage('Enter credentials before testing');
      return;
    }

    setOssTesting(true);
    setOssMessage('');

    try {
      const response = await apiService.post('/settings/oss/test', {
        clientId: ossClientId,
        clientSecret: ossClientSecret || '__USE_STORED__' // Server should use stored if not provided
      });

      if (response.success) {
        setOssMessage('Connection successful! Token expires in ' + response.expiresIn + 's');
        setOssTestStatus('success');
        setOssLastTested(new Date().toISOString());
      } else {
        setOssMessage(response.error || 'Test failed');
        setOssTestStatus('failed');
      }
      setTimeout(() => setOssMessage(''), 5000);
    } catch (error: any) {
      setOssMessage(error.message || 'Test failed');
      setOssTestStatus('failed');
    } finally {
      setOssTesting(false);
    }
  };

  if (!isVisible) return null;

  const handleSaveProfile = async () => {
    if (!user) return;

    setIsSaving(true);
    setSaveMessage('');

    try {
      await apiService.put(`/users/${user.id}`, {
        name,
        email,
        organization
      });

      // Refresh the user data
      await refreshToken();

      setSaveMessage(t('profileUpdatedSuccess'));
      setIsEditing(false);

      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error: any) {
      setSaveMessage(error.message || t('profileUpdateFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setName(user?.name || '');
    setEmail(user?.email || '');
    setOrganization(user?.organization || '');
    setIsEditing(false);
    setSaveMessage('');
  };

  const handleChangePassword = async () => {
    if (!user) return;

    // Validate fields
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordMessage(t('allFieldsRequired'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordMessage(t('newPasswordsDoNotMatch'));
      return;
    }

    if (newPassword.length < 8) {
      setPasswordMessage(t('passwordMinLength'));
      return;
    }

    const strength = calculatePasswordStrength(newPassword);
    if (!strength.isValid) {
      setPasswordMessage(t('passwordTooWeak') + ' ' + strength.feedback.join(', '));
      return;
    }

    setIsChangingPassword(true);
    setPasswordMessage('');

    try {
      await apiService.put('/auth/change-password', {
        currentPassword,
        newPassword
      });

      setPasswordMessage(t('passwordChangedSuccess'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordForm(false);

      setTimeout(() => setPasswordMessage(''), 3000);
    } catch (error: any) {
      setPasswordMessage(error.message || t('passwordChangeFailed'));
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleCancelPasswordChange = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowPasswordForm(false);
    setPasswordMessage('');
  };

  // Calculate password strength for display
  const passwordStrength = newPassword ? calculatePasswordStrength(newPassword) : null;

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="dt-body">
      <div className="dt-phead">
        <div className="pic">{IC_SETTINGS}</div>
        <div>
          <h2>{t('settingsTitle')}</h2>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 760 }}>
        {/* User Profile Section */}
        <div className="dt-card">
          <div className="h">
            <h3>{t('profileInfo')}</h3>
            <span className="a" style={{ display: 'flex', gap: 8 }}>
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="dt-btn sm"
                >
                  {t('editProfile')}
                </button>
              ) : (
                <>
                  <button
                    onClick={handleCancel}
                    disabled={isSaving}
                    className="dt-btn sm"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    onClick={handleSaveProfile}
                    disabled={isSaving}
                    className="dt-btn pri sm"
                    style={{ opacity: isSaving ? 0.6 : 1 }}
                  >
                    {isSaving ? t('saving') : t('saveChanges')}
                  </button>
                </>
              )}
            </span>
          </div>
          <div className="c">
            <div className="dt-grid2" style={{ marginBottom: 12 }}>
              {/* Name Field */}
              <div>
                <label className="dt-flabel">{t('name')}</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!isEditing}
                  className="dt-field"
                />
              </div>

              {/* Email Field */}
              <div>
                <label className="dt-flabel">{t('email')}</label>
                <input
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={!isEditing}
                  className="dt-field"
                />
              </div>
            </div>

            {/* Organization Field */}
            <div>
              <label className="dt-flabel">{t('organization')}</label>
              <input
                type="text"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
                disabled={!isEditing}
                className="dt-field"
                placeholder={t('yourOrganizationName')}
              />
            </div>

            {/* Save Message */}
            {saveMessage && (
              <div style={{
                padding: '10px 12px',
                marginTop: 14,
                background: saveMessage.includes('success') ? 'var(--dt-good-t)' : 'var(--dt-bad-t)',
                border: `1px solid ${saveMessage.includes('success') ? 'var(--dt-good)' : 'var(--dt-bad)'}`,
                borderRadius: 8,
                color: saveMessage.includes('success') ? 'var(--dt-good)' : 'var(--dt-bad)',
                fontSize: 14
              }}>
                {saveMessage}
              </div>
            )}
          </div>
        </div>

        {/* Password Change Section */}
        <div className="dt-card">
          <div className="h">
            <h3>{t('password')}</h3>
          </div>
          <div className="c">
            {!showPasswordForm ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12
              }}>
                <p style={{ fontSize: 14, color: 'var(--dt-text-2)', margin: 0 }}>
                  {t('keepAccountSecure')}
                </p>
                <button
                  onClick={() => setShowPasswordForm(true)}
                  className="dt-btn pri sm"
                >
                  {t('changePassword')}
                </button>
              </div>
            ) : (
              <div>
                {/* Current Password */}
                <div style={{ marginBottom: 12 }}>
                  <label className="dt-flabel">{t('currentPassword')}</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="dt-field"
                    placeholder={t('enterCurrentPassword')}
                  />
                </div>

                <div className="dt-grid2" style={{ marginBottom: 10 }}>
                  {/* New Password */}
                  <div>
                    <label className="dt-flabel">{t('newPassword')}</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="dt-field"
                      placeholder={t('enterNewPassword')}
                    />
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="dt-flabel">{t('confirmNewPassword')}</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="dt-field"
                      placeholder={t('reenterNewPassword')}
                    />
                  </div>
                </div>

                {/* Password Strength Indicator */}
                {passwordStrength && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      marginBottom: 4
                    }}>
                      <div className="dt-pbar" style={{ flex: 1 }}>
                        <i style={{
                          width: `${(passwordStrength.score / 4) * 100}%`,
                          background: passwordStrength.color
                        }} />
                      </div>
                      <span style={{
                        fontSize: '.78rem',
                        fontWeight: 600,
                        color: passwordStrength.color
                      }}>
                        {passwordStrength.label}
                      </span>
                    </div>
                    {passwordStrength.feedback.length > 0 && (
                      <p style={{ fontSize: 12, color: 'var(--dt-text-3)', margin: 0 }}>
                        {passwordStrength.feedback.join(', ')}
                      </p>
                    )}
                  </div>
                )}

                {confirmPassword && newPassword !== confirmPassword && (
                  <p style={{ fontSize: 12, color: 'var(--dt-bad)', marginTop: 0, marginBottom: 12 }}>
                    {t('passwordsDoNotMatch')}
                  </p>
                )}

                {/* Password Message */}
                {passwordMessage && (
                  <div style={{
                    padding: '10px 12px',
                    marginBottom: 12,
                    background: passwordMessage.includes('success') ? 'var(--dt-good-t)' : 'var(--dt-bad-t)',
                    border: `1px solid ${passwordMessage.includes('success') ? 'var(--dt-good)' : 'var(--dt-bad)'}`,
                    borderRadius: 8,
                    color: passwordMessage.includes('success') ? 'var(--dt-good)' : 'var(--dt-bad)',
                    fontSize: 14
                  }}>
                    {passwordMessage}
                  </div>
                )}

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={handleChangePassword}
                    disabled={isChangingPassword || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                    className="dt-btn pri sm"
                    style={{
                      opacity: (isChangingPassword || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword) ? 0.6 : 1
                    }}
                  >
                    {isChangingPassword ? t('changing') : t('changePassword')}
                  </button>
                  <button
                    onClick={handleCancelPasswordChange}
                    disabled={isChangingPassword}
                    className="dt-btn sm"
                  >
                    {t('cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Appearance & preferences (Theme + Language) */}
        <div className="dt-card">
          <div className="h">
            <h3>{t('appearance')}</h3>
          </div>
          <div className="c">
            {/* Theme */}
            <div className="dt-setrow">
              <div className="g">
                <div className="t">{t('themeMode')}</div>
                <div className="d">{t('currentlyUsing')} {mode === 'light' ? t('light') : t('dark')} {t('theme')}</div>
              </div>
              <button onClick={toggleTheme} className="dt-btn sm">
                {t('switchTo')} {mode === 'light' ? t('dark') : t('light')}
              </button>
            </div>

            {/* Interface language */}
            <div className="dt-setrow">
              <div className="g">
                <div className="t">{t('interfaceLanguage')}</div>
                <div className="d">{t('current')}: {currentLanguage === 'en' ? 'English' : 'עברית'}</div>
              </div>
              <button
                onClick={() => setLanguage(currentLanguage === 'en' ? 'he' : 'en')}
                className="dt-btn sm"
              >
                <span>{currentLanguage === 'en' ? 'HE' : 'EN'}</span>
                {' '}{t('switchToLanguage')} {currentLanguage === 'en' ? 'עברית' : 'English'}
              </button>
            </div>

            {/* Asset icons on 2D map & minimap (from main — server-persisted per-user pref) */}
            <div className="dt-setrow">
              <div className="g">
                <div className="t">{currentLanguage === 'he' ? 'אייקוני נכסים במפה הדו-ממדית ובמיני-מפה' : 'Asset icons on 2D map & minimap'}</div>
                <div className="d">{currentLanguage === 'he' ? 'אייקוני הפנורמה אינם מושפעים מהגדרה זו' : 'Panorama icons are not affected by this setting'}</div>
              </div>
              <button
                onClick={() => setShowAssetIcons2D(!showAssetIcons2D)}
                className={`dt-btn sm${showAssetIcons2D ? ' pri' : ''}`}
              >
                {showAssetIcons2D ? (currentLanguage === 'he' ? 'פעיל' : 'On') : (currentLanguage === 'he' ? 'כבוי' : 'Off')}
              </button>
            </div>
          </div>
        </div>

        {/* ACC Translation Settings - Admin Only */}
        {isAdmin && (
          <div className="dt-card">
            <div className="h">
              <h3>
                ACC Translation Settings
                <span className="dt-chip" style={{ color: 'var(--dt-bad)', background: 'var(--dt-bad-t)', marginInlineStart: 8 }}>ADMIN</span>
              </h3>
            </div>
            <div className="c">
              <p style={{ fontSize: 13, color: 'var(--dt-text-2)', marginBottom: 16, lineHeight: 1.5 }}>
                Configure Autodesk Platform Services (APS) credentials for translating RVT files when no existing translation exists in ACC.
                These credentials must have <strong>Data Management API</strong> access enabled.
              </p>

              {/* Status indicator */}
              {ossConfigured && (
                <div className="dt-facc" style={{ marginBottom: 16 }}>
                  <span className="dt-dot-on" style={{
                    background: ossTestStatus === 'success' ? 'var(--dt-good)' : ossTestStatus === 'failed' ? 'var(--dt-bad)' : 'var(--dt-info)'
                  }} />
                  <span style={{
                    color: ossTestStatus === 'success' ? 'var(--dt-good)' : ossTestStatus === 'failed' ? 'var(--dt-bad)' : 'var(--dt-info)'
                  }}>
                    {ossTestStatus === 'success' ? 'Credentials verified' : ossTestStatus === 'failed' ? 'Credentials failed verification' : 'Credentials configured (not tested)'}
                    {ossLastTested && (
                      <span style={{ marginLeft: '8px', opacity: 0.7, fontSize: '11px' }}>
                        Last tested: {new Date(ossLastTested).toLocaleString()}
                      </span>
                    )}
                  </span>
                </div>
              )}

              <div className="dt-grid2" style={{ marginBottom: 16 }}>
                {/* Client ID */}
                <div>
                  <label className="dt-flabel">APS Client ID</label>
                  <input
                    type="text"
                    value={ossClientId}
                    onChange={(e) => setOssClientId(e.target.value)}
                    className="dt-field"
                    placeholder="Enter Autodesk Platform Services Client ID"
                    style={{ fontFamily: 'ui-monospace, monospace' }}
                  />
                </div>

                {/* Client Secret */}
                <div>
                  <label className="dt-flabel">
                    APS Client Secret {ossHasSecret && <span style={{ color: 'var(--dt-good)', fontWeight: 'normal' }}>(stored)</span>}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showOssSecret ? 'text' : 'password'}
                      value={ossClientSecret}
                      onChange={(e) => setOssClientSecret(e.target.value)}
                      className="dt-field"
                      placeholder={ossHasSecret ? 'Enter new secret to update, or leave blank' : 'Enter Autodesk Platform Services Client Secret'}
                      style={{ paddingRight: 40, fontFamily: 'ui-monospace, monospace' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowOssSecret(!showOssSecret)}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '16px',
                        opacity: 0.6
                      }}
                    >
                      {showOssSecret ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Message */}
              {ossMessage && (
                <div style={{
                  padding: '10px 12px',
                  marginBottom: 16,
                  background: ossMessage.includes('success') || ossMessage.includes('Connection successful') ? 'var(--dt-good-t)' : 'var(--dt-bad-t)',
                  border: `1px solid ${ossMessage.includes('success') || ossMessage.includes('Connection successful') ? 'var(--dt-good)' : 'var(--dt-bad)'}`,
                  borderRadius: 8,
                  color: ossMessage.includes('success') || ossMessage.includes('Connection successful') ? 'var(--dt-good)' : 'var(--dt-bad)',
                  fontSize: 14
                }}>
                  {ossMessage}
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={handleTestOssCredentials}
                  disabled={ossTesting || (!ossClientId || (!ossClientSecret && !ossHasSecret))}
                  className="dt-btn sm"
                  style={{ opacity: ossTesting || (!ossClientId || (!ossClientSecret && !ossHasSecret)) ? 0.6 : 1 }}
                >
                  {ossTesting ? 'Testing...' : 'Test Connection'}
                </button>
                <button
                  onClick={handleSaveOssSettings}
                  disabled={ossSaving || !ossClientId || !ossClientSecret}
                  className="dt-btn pri sm"
                  style={{ opacity: ossSaving || !ossClientId || !ossClientSecret ? 0.6 : 1 }}
                >
                  {ossSaving ? 'Saving...' : 'Save Credentials'}
                </button>
              </div>

              <p style={{ fontSize: 11, color: 'var(--dt-text-3)', marginTop: 16, marginBottom: 0 }}>
                Get credentials from <a href="https://aps.autodesk.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--dt-accent)', fontWeight: 600 }}>aps.autodesk.com</a>.
                Ensure Data Management API is enabled for the app.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleSettingsPanel;
